import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { useForm, FormProvider } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import instance from '../../axios-instance';
import { Flex } from '@mantine/core';
import {
  IoInformationCircleOutline,
  IoHelpCircleOutline,
} from 'react-icons/io5';
import { Input, Textarea, CustomRadioInput, CustomButton } from '@jadwalh/ui';
import { getLangState } from '../../store/lang.slice';
import {
  ReportAbuseWrapper,
  ReportHeader,
  ReportContent,
  InfoSection,
  BackButton,
} from './styles';
import FullLogo from '../../assets/images/svg/FullLogo.svg';

interface ReportAbuseData {
  abuseType: string;
  email: string;
  additionalDetails: string;
}

export default function ReportAbusePage() {
  const { formatMessage } = useIntl();
  const langType = useSelector(getLangState);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [selectedAbuseType, setSelectedAbuseType] = useState('spam');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const client = searchParams.get('client');
  const event = searchParams.get('event');

  const methods = useForm<ReportAbuseData>({
    defaultValues: {
      abuseType: 'spam',
      email: '',
      additionalDetails: '',
    },
    mode: 'onBlur',
  });

  const { handleSubmit, setValue, watch, formState: { errors }, register } = methods;

  const abuseTypes = [
    {
      value: 'spam',
      label: formatMessage({ id: 'reportAbuse.spamUnsolicited' }),
      hint: formatMessage({ id: 'reportAbuse.spamHint' }),
      hintText: formatMessage({ id: 'reportAbuse.spamHintText' }),
    },
    {
      value: 'phishing',
      label: formatMessage({ id: 'reportAbuse.phishingMalware' }),
      hint: formatMessage({ id: 'reportAbuse.phishingHint' }),
      hintText: formatMessage({ id: 'reportAbuse.phishingHintText' }),
    },
    {
      value: 'fraud',
      label: formatMessage({ id: 'reportAbuse.fraudScamImpersonation' }),
      hint: formatMessage({ id: 'reportAbuse.fraudHint' }),
      hintText: formatMessage({ id: 'reportAbuse.fraudHintText' }),
    },
    {
      value: 'hatred',
      label: formatMessage({ id: 'reportAbuse.hateViolenceIllegal' }),
      hint: formatMessage({ id: 'reportAbuse.hateHint' }),
      hintText: formatMessage({ id: 'reportAbuse.hateHintText' }),
    },
    {
      value: 'other',
      label: formatMessage({ id: 'reportAbuse.other' }),
      hint: formatMessage({ id: 'reportAbuse.otherHint' }),
      hintText: formatMessage({ id: 'reportAbuse.otherHintText' }),
    },
  ];

  const handleAbuseTypeChange = (value: string) => {
    setSelectedAbuseType(value);
    setValue('abuseType', value);
  };

  const onFormSubmit = async (data: ReportAbuseData) => {
    setIsSubmitting(true);
    try {
      const eventLink = `${window.location.origin}/${client}/${event}`;

      const payload: any = {
        abuse_type: data.abuseType,
        event_link: eventLink,
      };

      // Only add optional fields if they have values
      if (data.additionalDetails && data.additionalDetails.trim() !== '') {
        payload.details = data.additionalDetails;
      }

      if (data.email && data.email.trim() !== '') {
        payload.email = data.email;
      }

      const response = await instance.post('/general/abuse-report', payload);

      // Navigate to success page with message from response
      const responseMessage =
        response.data?.message ||
        formatMessage({ id: 'reportAbuse.submitSuccess' });
      navigate(
        `/report-abuse-success?message=${encodeURIComponent(responseMessage)}`
      );
    } catch (error) {
      alert(formatMessage({ id: 'reportAbuse.submitError' }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedType = abuseTypes.find(
    (type) => type.value === selectedAbuseType
  );

  const handleBack = () => {
    window.close();
  };

  return (
    <ReportAbuseWrapper lang={langType}>
      <ReportHeader lang={langType}>
        <div className="header-content">
          <img src={FullLogo} alt="Jadwalh" className="logo" />
          <h1>{formatMessage({ id: 'reportAbuse.title' })}</h1>
          <p className="description">
            {formatMessage({ id: 'reportAbuse.description' })}
          </p>
        </div>
      </ReportHeader>

      <ReportContent lang={langType}>
        <div className="info-notice">
          <IoInformationCircleOutline className="info-icon" />
          <p>{formatMessage({ id: 'reportAbuse.impactNotice' })}</p>
        </div>

        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onFormSubmit)}>
            <Flex direction="row" gap="40px" className="main-content">
              {/* Mobile Info Section - Shows above form on small screens */}
              <InfoSection className="info-section mobile-info" lang={langType}>
                {selectedType && (
                  <div className="info-card">
                    <h4>
                      <IoHelpCircleOutline className="info-icon" />
                      {selectedType.hint}
                    </h4>
                    <div className="info-content">
                      <p>{selectedType.hintText}</p>
                    </div>
                  </div>
                )}
              </InfoSection>

              <div className="form-section">
                <div className="form-group">
                  <label className="form-label">
                    {formatMessage({ id: 'reportAbuse.typeOfAbuse' })} *
                  </label>
                  <div className="radio-group">
                    {abuseTypes.map((type) => (
                      <div key={type.value} className="radio-option">
                        <CustomRadioInput
                          value={type.value}
                          label={type.label}
                          checked={selectedAbuseType === type.value}
                          onChange={() => handleAbuseTypeChange(type.value)}
                          name="abuseType"
                        />
                      </div>
                    ))}
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">
                    {formatMessage({
                      id: 'reportAbuse.shareAdditionalDetails',
                    })}
                  </label>
                  <Textarea
                    name="additionalDetails"
                    placeholder={formatMessage({
                      id: 'reportAbuse.additionalDetailsPlaceholder',
                    })}
                    maxLength={1000}
                  />
                </div>

                <div className="form-group">
                  <Input
                    {...register('email', {
                      pattern: {
                        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                        message: formatMessage({ id: 'invalidEmail' }),
                      },
                    })}
                    type="text"
                    label={formatMessage({ id: 'reportAbuse.yourEmail' })}
                    placeholder={formatMessage({
                      id: 'reportAbuse.emailPlaceholder',
                    })}
                  />
                </div>

                <div
                  style={{ marginTop: '24px', display: 'flex', gap: '12px' }}
                >
                  <CustomButton
                    type="submit"
                    disabled={!selectedAbuseType || isSubmitting}
                    label={
                      isSubmitting
                        ? formatMessage({ id: 'reportAbuse.submitting' })
                        : formatMessage({ id: 'reportAbuse.submit' })
                    }
                    statusColor="#FFFFFF"
                    borderColor="#000000"
                    BGColor="#000000"
                    fullWidth={true}
                    fontSize="16px"
                    isLoading={isSubmitting}
                  />
                  <CustomButton
                    type="button"
                    onClick={() => window.close()}
                    label={formatMessage({ id: 'cancel' })}
                    statusColor="#000000"
                    borderColor="#E0E0E0"
                    BGColor="#FFFFFF"
                    fullWidth={true}
                    fontSize="16px"
                  />
                </div>
              </div>

              {/* Desktop Info Section - Shows on the right on large screens */}
              <InfoSection
                className="info-section desktop-info"
                lang={langType}
              >
                {selectedType && (
                  <div className="info-card">
                    <h4>
                      <IoHelpCircleOutline className="info-icon" />
                      {selectedType.hint}
                    </h4>
                    <div className="info-content">
                      <p>{selectedType.hintText}</p>
                    </div>
                  </div>
                )}
              </InfoSection>
            </Flex>
          </form>
        </FormProvider>
      </ReportContent>
    </ReportAbuseWrapper>
  );
}
